import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Badge, Card, Skeleton } from '@/components/ui';
import { HomeEventCard } from '@/components/home/HomeEventCard';
import { useEvents } from '@/hooks/useEvents';
import { groupEvents, type GroupDimension } from '@/lib/eventGrouping';

/**
 * GroupedCatalog — catálogo de eventos AGRUPADO por categoría o ciudad
 * (`/eventos/categorias`, `/eventos/ciudades`).
 *
 * Reusa `useEvents()` (misma query/caché que el listado principal `/eventos`),
 * así que no dispara un fetch nuevo si el usuario ya pasó por el catálogo, y
 * agrupa en el cliente con `groupEvents`. Cada grupo es un encabezado con su
 * contador + una grilla de `HomeEventCard`, idéntica a la del catálogo, para
 * mantener la coherencia visual premium. Maneja carga (skeletons), error y
 * vacío igual que `EventsPage`.
 */
interface GroupedCatalogProps {
  dimension: GroupDimension;
  heading: string;
  subtitle: string;
  /** Etiqueta para el grupo de eventos sin categoría/ciudad asignada. */
  fallbackLabel: string;
}

export function GroupedCatalog({
  dimension,
  heading,
  subtitle,
  fallbackLabel,
}: GroupedCatalogProps): JSX.Element {
  const { t } = useTranslation('events');
  const { data: events, isLoading, isError } = useEvents();

  const groups = useMemo(
    () => (events ? groupEvents(events, dimension, fallbackLabel) : []),
    [events, dimension, fallbackLabel],
  );

  return (
    <div>
      {/* Encabezado de página — tipografía display + subtítulo muted (paleta premium). */}
      <header className="border-b border-fc-line bg-fc-paper">
        <div className="container-fc py-10 md:py-14">
          <h1 className="font-display text-3xl font-semibold text-fc-ink md:text-4xl">{heading}</h1>
          <p className="mt-2 max-w-2xl text-fc-muted">{subtitle}</p>
        </div>
      </header>

      <section className="container-fc py-10 md:py-14">
        {isLoading ? (
          <GroupedSkeleton />
        ) : isError ? (
          <Card variant="outlined" padded>
            <p className="text-sm text-fc-muted" role="alert">
              {t('list.error')}
            </p>
          </Card>
        ) : groups.length === 0 ? (
          <Card variant="outlined" padded>
            <p className="text-sm text-fc-muted">
              {t('grouped.empty')}{' '}
              <Link to="/eventos" className="text-fc-ink underline hover:text-fc-ink/70">
                {t('grouped.emptyLink')}
              </Link>
              .
            </p>
          </Card>
        ) : (
          <div className="flex flex-col gap-12">
            {groups.map((group) => (
              <section key={group.key} aria-labelledby={`grp-${group.key}`}>
                <div className="mb-5 flex items-baseline justify-between gap-3 border-b border-fc-line pb-3">
                  <h2
                    id={`grp-${group.key}`}
                    className="font-display text-2xl font-semibold text-fc-ink"
                  >
                    {group.label}
                  </h2>
                  <Badge tone="neutral">
                    {t('grouped.eventCount', { count: group.events.length })}
                  </Badge>
                </div>
                {/* Carrusel horizontal (estilo Netflix): scroll-snap + scrollbar
                    oculta; las cards no se encogen (ancho fijo + shrink-0). */}
                <div className="flex snap-x snap-mandatory gap-6 overflow-x-auto scrollbar-hide pb-2">
                  {group.events.map((evt) => (
                    <div key={evt.id} className="w-72 shrink-0 snap-start">
                      <HomeEventCard event={evt} />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/** Skeleton de dos grupos mientras carga (misma silueta que la grilla real). */
function GroupedSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-12" aria-busy="true">
      {[0, 1].map((g) => (
        <div key={g}>
          <Skeleton shape="h-7 w-48" className="mb-5" srLabel="Cargando eventos…" />
          <div className="flex gap-6 overflow-x-auto scrollbar-hide pb-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="flex w-72 shrink-0 flex-col overflow-hidden rounded-card border border-fc-line bg-fc-paper shadow-sm"
              >
                <Skeleton shape="aspect-[16/10] w-full rounded-none" srLabel="" />
                <div className="flex flex-1 flex-col gap-2 p-4 md:p-5">
                  <Skeleton shape="h-5 w-4/5" srLabel="" />
                  <Skeleton shape="h-4 w-2/3" srLabel="" />
                  <div className="mt-4 flex items-center justify-between border-t border-fc-line/70 pt-4">
                    <Skeleton shape="h-5 w-24" srLabel="" />
                    <Skeleton shape="h-5 w-20" srLabel="" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
